import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

import { SEQUENCE_PREFIXES, type Shortcut } from '@/config/shortcuts';
import { employeeTargetFor } from '@/config/employeeExperience';
import { sequenceShortcutsFor, shortcutGroupsFor } from '@/config/shortcutAccess';
import { useAuthStore } from '@/stores/authStore';

interface Props {
  /** Opens the global search overlay, which AppLayout owns. Same wiring as
   *  QuickActions — this component cannot reach that state itself. */
  onOpenSearch?: () => void;
  /** Opens the Quick Actions panel, for completeness; QuickActions also binds
   *  Q itself, so this stays optional. */
  onOpenQuickActions?: () => void;
}

export default function KeyboardShortcuts({ onOpenSearch, onOpenQuickActions }: Props) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  // Employee rebuild C General: only the shortcuts this person may use — the
  // handler and the panel from the same filter, so a key that is not listed
  // does nothing and a listed key always works.
  const user = useAuthStore((s) => s.user);
  const sequences = useMemo(() => sequenceShortcutsFor(user), [user]);
  const groups = useMemo(() => shortcutGroupsFor(user), [user]);

  // A ref, not state: the previous version kept the pending prefix in state and
  // listed it in the deps of the handler, so every keystroke tore the listener
  // down and re-added it. A ref also means the timeout below always clears the
  // prefix it set, rather than a stale one.
  const pendingKey = useRef<string | null>(null);
  const pendingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = useCallback(() => {
    pendingKey.current = null;
    if (pendingTimer.current) {
      clearTimeout(pendingTimer.current);
      pendingTimer.current = null;
    }
  }, []);

  const run = useCallback(
    (shortcut: Shortcut) => {
      switch (shortcut.action.type) {
        case 'navigate':
          navigate(employeeTargetFor(user, shortcut.action.to));
          break;
        case 'openSearch':
          onOpenSearch?.();
          break;
        case 'openQuickActions':
          onOpenQuickActions?.();
          break;
        case 'toggleHelp':
          setIsOpen((prev) => !prev);
          break;
        case 'dismissTopmost':
          // Deliberately nothing: each overlay closes itself.
          break;
      }
    },
    [navigate, user, onOpenSearch, onOpenQuickActions],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isInput =
        !!target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          target.isContentEditable);

      // Never steal a keystroke from a field the user is typing in, and never
      // claim a modifier combo — those belong to the browser or to AppLayout.
      if (isInput || e.metaKey || e.ctrlKey || e.altKey) {
        clearPending();
        return;
      }

      // Second key of a sequence, e.g. G then D, C then T.
      if (pendingKey.current) {
        const prefix = pendingKey.current;
        const key = e.key.toUpperCase();
        clearPending();
        const shortcut = sequences.find(
          (s) => s.keys[0] === prefix && s.keys[1] === key,
        );
        if (shortcut) {
          e.preventDefault();
          run(shortcut);
        }
        return;
      }

      // '?' is Shift+/ on most layouts, so it must be tested BEFORE plain '/'
      // or the help panel would never open — and '/' alone focuses search.
      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (e.key === '/') {
        e.preventDefault();
        onOpenSearch?.();
        return;
      }

      // Start of a sequence.
      const upper = e.key.toUpperCase();
      if ((SEQUENCE_PREFIXES as readonly string[]).includes(upper)) {
        pendingKey.current = upper;
        pendingTimer.current = setTimeout(clearPending, 1000);
      }
    },
    [clearPending, run, onOpenSearch, sequences],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => clearPending, [clearPending]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[70]" onClose={() => setIsOpen(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl bg-surface p-6 shadow-2xl">
              <div className="mb-5 flex items-center justify-between">
                <Dialog.Title className="text-lg font-semibold text-content">
                  Keyboard Shortcuts
                </Dialog.Title>
                <button
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg p-1.5 text-content-subtle hover:bg-surface-muted"
                  aria-label="Close"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Rendered from config/shortcuts.ts, exactly as the Settings
                  page renders it. Neither list is written out by hand any
                  more, so they cannot drift apart again. */}
              <div className="space-y-1">
                {groups.map((group) => (
                  <Fragment key={group.title}>
                    <p className="pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-content-subtle">
                      {group.title}
                    </p>
                    {group.items.map((shortcut) => (
                      <div
                        key={shortcut.label}
                        className="flex items-center justify-between rounded-lg px-3 py-1.5"
                      >
                        <span className="text-sm text-content-tertiary">{shortcut.label}</span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((k, i) => (
                            <Fragment key={k}>
                              {i > 0 && (
                                <span className="text-[10px] text-content-subtle">
                                  {shortcut.keys[0] === 'Ctrl/Cmd' ? '+' : 'then'}
                                </span>
                              )}
                              <kbd className="rounded bg-surface-strong px-1.5 py-0.5 font-mono text-xs">
                                {k}
                              </kbd>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </Fragment>
                ))}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
